import { Button } from '@/components/ui/button'
import { Form, MainFormErrorMessage } from '@/components/ui/form'
import { FormInput } from '@/components/ui/form/FormInput'
import { useActiveBusiness } from '@/hooks/useCurrentActiveBusiness'
import { orpc } from '@/lib/orpc/client'
import { queryClient } from '@/lib/query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, PenTool } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

// Zod schema for manual article form validation
const manualArticleSchema = z.object({
  title: z
    .string()
    .min(1, 'Article title is required')
    .max(200, 'Title must be less than 200 characters'),
})

type ManualArticleFormData = z.infer<typeof manualArticleSchema>

export const Route = createFileRoute('/_dashboard/write/manual')({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate()
  const { activeBusiness } = useActiveBusiness()
  const form = useForm<ManualArticleFormData>({
    resolver: zodResolver(manualArticleSchema),
    defaultValues: {
      title: '',
    },
  })
  const createArticleFromTitleMutation = useMutation(
    orpc.articles.createArticleFromTitle.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.articles.getListOfArticles.queryKey({ input: {} }),
        })
      },
    }),
  )

  const handleSubmit = async (data: ManualArticleFormData) => {
    if (!activeBusiness?.id) {
      form.setError('root', { message: 'Please select a business first.' })
      return
    }
    try {
      const res = await createArticleFromTitleMutation.mutateAsync({
        title: data.title,
      })
      await navigate({
        to: '/edit/$articleId',
        params: { articleId: res.id },
      })
    } catch (e) {
      form.setError('root', {
        message: e instanceof Error ? e.message : 'Failed to create article',
      })
    }
  }

  // Draft/Preview handlers will be implemented later

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl">
        <Link
          to="/write"
          className="flex gap-2 items-center mb-6 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to options
        </Link>

        <div className="space-y-6">
          <div className="space-y-4 text-center">
            <div className="flex justify-center">
              <div className="p-3 rounded-full bg-primary/10">
                <PenTool className="w-8 h-8 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold">Manual Article Editor</h1>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Write your article from scratch with our rich text editor. Save
              drafts, preview your work, and publish when ready.
            </p>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="p-6 space-y-4 rounded-lg border bg-card max-w-xl mx-auto"
            >
              <FormInput
                control={form.control}
                name="title"
                label="Article Title"
                placeholder="Enter your article title..."
              />

              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting
                    ? 'Creating...'
                    : 'Create Article'}
                </Button>
              </div>
              <MainFormErrorMessage />
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
